import type React from 'react';

interface ButtonRoundingClasses {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  full: string;
}

interface ButtonSizeClasses {
  timestamp: string;
  pico: string;
  tiny: string;
  xs: string;
  sm: string;
  md: string;
  large: string;
}

interface ButtonTProps {
  type: 'button' | 'submit';
  role?: 'button' | 'submit';
  styling: keyof typeof buttonClasses;
  rounding?: keyof ButtonRoundingClasses;
  size?: keyof ButtonSizeClasses;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  shouldOverrideTextSize?: boolean;
}

const buttonClasses = {
  primary: 'text-white !bg-primary-base font-light hover:!bg-opacity-90',
  'primary-dark': 'text-white !bg-primary-dark font-light hover:!bg-opacity-90',
  secondary: 'text-white bg-gray-400 font-light hover:!bg-opacity-90',
  danger: 'text-white bg-danger font-light hover:!bg-opacity-90',
  warning: 'text-white bg-warning font-light hover:!bg-opacity-90',
  info: 'text-white bg-primary-base font-light hover:!bg-opacity-90',
  success: 'text-white bg-success font-light hover:!bg-opacity-90',
  red: 'text-white bg-red-500 font-light hover:!bg-opacity-90',
  green: 'text-white bg-green-500 font-light hover:!bg-opacity-90',
  cancel:
    'text-primary-base bg-white font-light border border-primary-base hover:bg-gray-100',
  gray: 'text-white bg-gray-300 font-light hover:bg-gray-400',
  transparent:
    'text-gray-500 bg-transparent font-light hover:text-gray-600 !shadow-none',
  teal: 'text-teal-700 bg-teal-50 font-light hover:bg-teal-100 border border-teal-300',
  amber: 'text-white bg-amber-500 font-light hover:bg-amber-600 !shadow-none',
  outline:
    'text-gray-700 bg-white font-light border border-gray-300 hover:bg-gray-50',
  filter:
    'text-gray-600 bg-white font-light border border-gray-200 hover:bg-gray-50 !shadow-none',
  'outline-red':
    'text-red-500 bg-red-50 font-light border border-red-300 hover:bg-red-100',
  'outline-blue':
    'text-blue-600 bg-blue-50 font-light border border-blue-200 hover:bg-blue-100',
  'outline-blue-400':
    'text-blue-500 bg-blue-50 font-light border border-blue-100 hover:bg-blue-100',
  'outline-green':
    'text-green-700 bg-green-50 font-light border border-green-300 hover:bg-green-100',
  'outline-sky':
    'text-sky-700 bg-white font-light border border-sky-300 hover:bg-sky-50',
  'outline-amber':
    'text-amber-700 bg-amber-50 font-light border border-amber-300 hover:bg-amber-100',
  'outline-purple':
    'text-purple-700 bg-purple-50 font-light border border-purple-300 hover:bg-purple-100',
  'outline-primary':
    'text-primary-base bg-white font-semibold border border-primary-base hover:bg-blue-50',
};

const ButtonT: React.FC<ButtonTProps> = ({
  type,
  role,
  styling,
  rounding,
  size,
  onClick,
  children,
  className = '',
  disabled = false,
  shouldOverrideTextSize,
}) => {
  const borderRadiusClasses = {
    xs: 'rounded-xs',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  const sizeClasses = {
    timestamp: '!text-timestamp px-1.5 py-0.5',
    pico: '!text-pico px-2 py-1',
    tiny: '!text-tiny px-2 py-1',
    xs: '!text-xs px-3 py-1.5',
    sm: '!text-sm px-3 py-3',
    md: '!text-base px-6 py-3',
    large: '!text-lg px-8 py-4',
  };

  const buttonClass = buttonClasses[styling] || '';
  const borderRadiusClass = rounding ? borderRadiusClasses[rounding] || '' : '';
  const sizeClass = size ? sizeClasses[size] || '' : 'px-4 py-2';
  const disabledClass = disabled
    ? 'opacity-50 cursor-default pointer-events-none'
    : '';

  if (!shouldOverrideTextSize && !size) {
    className += ' text-xs';
  }

  return (
    <button
      type={type}
      role={role || type}
      aria-disabled={disabled}
      className={`${buttonClass} ${borderRadiusClass} ${sizeClass} ${className} ${disabledClass} shadow-button select-none`}
      onClick={disabled ? () => {} : onClick}>
      {children}
    </button>
  );
};

export default ButtonT;
